import { useState, useEffect } from 'react';
import { X, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Agency {
  id: string;
  name: string;
  street: string;
  zip_code: string;
  city: string;
  phone: string;
  company: string;
  annual_objectives?: {
    fiscal_year: number;
    annual_objective: number;
    accounting_objective: number;
  }[];
  has_custom_objectives?: boolean;
  monthly_objectives?: Record<string, Record<string, number>>;
}

interface AgencyModalProps {
  agency: Agency | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MONTHS = [
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' }
];

export default function AgencyModal({ agency, isOpen, onClose, onSuccess }: AgencyModalProps) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return month <= 4 ? today.getFullYear() - 1 : today.getFullYear();
  });

  // Générer la liste des années fiscales de 2023-2024 à 2030-2031
  const fiscalYears = Array.from({ length: 8 }, (_, i) => 2023 + i);

  const [formData, setFormData] = useState({
    name: '',
    street: '',
    zip_code: '',
    city: '',
    phone: '',
    company: 'KI',
    has_custom_objectives: {} as Record<number, boolean>,
    monthly_objectives: {} as Record<string, Record<string, number>>,
    annual_objectives: {} as Record<number, { annual: number; accounting: number }>
  });

  useEffect(() => {
    if (agency) {
      loadAgencyObjectives();
    } else {
      resetForm();
    }
  }, [agency]);

  const loadAgencyObjectives = async () => {
    if (!agency) return;

    try {
      // Load annual objectives for all fiscal years
      const { data: annualObjectives, error: objectivesError } = await supabase
        .from('agency_annual_objectives')
        .select('fiscal_year, annual_objective, accounting_objective')
        .eq('agency_name', agency.name);

      if (objectivesError) throw objectivesError;

      // Convert to Record format
      const annualObjectivesMap = (annualObjectives || []).reduce((acc, obj) => {
        acc[obj.fiscal_year] = {
          annual: obj.annual_objective,
          accounting: obj.accounting_objective
        };
        return acc;
      }, {} as Record<number, { annual: number; accounting: number }>);

      // Initialize monthly objectives with all months set to 0
      const initialMonthlyObjectives = {} as Record<string, Record<string, number>>;
      const hasCustomObjectives = {} as Record<number, boolean>;

      fiscalYears.forEach(year => {
        // Check if this year has custom objectives in the existing data
        const yearKey = year.toString();
        const existingYearData = agency.monthly_objectives?.[yearKey];
        
        hasCustomObjectives[year] = Boolean(existingYearData);
        
        if (existingYearData) {
          // Use existing data, ensuring all months are present
          initialMonthlyObjectives[yearKey] = { ...existingYearData };
          MONTHS.forEach(month => {
            if (typeof initialMonthlyObjectives[yearKey][month.value] !== 'number') {
              initialMonthlyObjectives[yearKey][month.value] = 0;
            }
          });
        }
      });

      setFormData({
        name: agency.name,
        street: agency.street || '',
        zip_code: agency.zip_code || '',
        city: agency.city || '',
        phone: agency.phone || '',
        company: agency.company || 'KI',
        has_custom_objectives: hasCustomObjectives,
        monthly_objectives: initialMonthlyObjectives,
        annual_objectives: annualObjectivesMap
      });
    } catch (err: any) {
      console.error('Error loading agency objectives:', err);
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      street: '',
      zip_code: '',
      city: '',
      phone: '',
      company: 'KI',
      has_custom_objectives: {},
      monthly_objectives: {},
      annual_objectives: {}
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.name.trim()) {
        throw new Error('Le nom de l\'agence est requis');
      }

      // Prepare monthly objectives data
      const monthlyObjectives = {} as Record<string, Record<string, number>>;
      
      // Only include years that have custom objectives enabled
      Object.entries(formData.has_custom_objectives).forEach(([year, hasCustom]) => {
        if (hasCustom) {
          // Ensure all months are present with numeric values
          const yearData = {} as Record<string, number>;
          MONTHS.forEach(month => {
            yearData[month.value] = parseFloat(formData.monthly_objectives[year]?.[month.value]?.toString() || '0') || 0;
          });
          monthlyObjectives[year] = yearData;
        }
      });

      const agencyData = {
        name: formData.name.trim(),
        street: formData.street.trim(),
        zip_code: formData.zip_code.trim(),
        city: formData.city.trim(),
        phone: formData.phone.trim(),
        company: formData.company,
        has_custom_objectives: Object.values(formData.has_custom_objectives).some(v => v),
        monthly_objectives: Object.keys(monthlyObjectives).length > 0 ? monthlyObjectives : null
      };

      if (agency) {
        // Update existing agency
        const { error: updateError } = await supabase
          .from('agencies')
          .update(agencyData)
          .eq('name', agency.name);

        if (updateError) throw updateError;
      } else {
        // Create new agency
        const { error: createError } = await supabase
          .from('agencies')
          .insert([agencyData]);

        if (createError) throw createError;
      }

      // Update annual objectives for each fiscal year
      for (const [year, objectives] of Object.entries(formData.annual_objectives)) {
        const fiscalYear = parseInt(year);
        if (isNaN(fiscalYear)) continue;

        await supabase
          .from('agency_annual_objectives')
          .upsert({
            agency_name: formData.name,
            fiscal_year: fiscalYear,
            annual_objective: formData.has_custom_objectives[fiscalYear] 
              ? Object.values(formData.monthly_objectives[fiscalYear] || {}).reduce((sum, val) => sum + (val || 0), 0)
              : objectives.annual,
            accounting_objective: objectives.accounting
          }, {
            onConflict: 'agency_name,fiscal_year'
          });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthlyObjectiveChange = (month: string, value: string) => {
    const yearKey = selectedFiscalYear.toString();
    
    // Initialize the year's data if it doesn't exist
    if (!formData.monthly_objectives[yearKey]) {
      const initialMonthlyValues = MONTHS.reduce((acc, m) => {
        acc[m.value] = 0;
        return acc;
      }, {} as Record<string, number>);
      
      setFormData(prev => ({
        ...prev,
        monthly_objectives: {
          ...prev.monthly_objectives,
          [yearKey]: initialMonthlyValues
        }
      }));
    }

    setFormData(prev => ({
      ...prev,
      monthly_objectives: {
        ...prev.monthly_objectives,
        [yearKey]: {
          ...prev.monthly_objectives[yearKey],
          [month]: parseFloat(value) || 0
        }
      }
    }));
  };

  if (!isOpen) return null;

  // Ensure all months exist with at least 0 values for the selected year
  if (formData.has_custom_objectives[selectedFiscalYear] && !formData.monthly_objectives[selectedFiscalYear]) {
    const initialMonthlyValues = MONTHS.reduce((acc, month) => {
      acc[month.value] = 0;
      return acc;
    }, {} as Record<string, number>);

    setFormData(prev => ({
      ...prev,
      monthly_objectives: {
        ...prev.monthly_objectives,
        [selectedFiscalYear]: initialMonthlyValues
      }
    }));
  }

  // Calculer les totaux pour l'année sélectionnée
  const yearTotal = formData.has_custom_objectives[selectedFiscalYear]
    ? Object.values(formData.monthly_objectives[selectedFiscalYear] || {})
        .reduce((sum, value) => sum + (parseFloat(value.toString()) || 0), 0)
    : formData.annual_objectives[selectedFiscalYear]?.annual || 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl relative max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-white/80" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">
                {agency ? "Modifier l'agence" : 'Nouvelle agence'}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-5">

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Société</label>
              <select
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                required
              >
                <option value="KI">KI</option>
                <option value="KA">KA</option>
                <option value="DRYADE">DRYADE</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'agence</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                required
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <select
                    value={selectedFiscalYear}
                    onChange={(e) => setSelectedFiscalYear(parseInt(e.target.value))}
                    className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                  >
                    {fiscalYears.map(year => (
                      <option key={year} value={year}>Année {year} - {year + 1}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="customObjectives"
                    checked={formData.has_custom_objectives[selectedFiscalYear] || false}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      has_custom_objectives: { ...prev.has_custom_objectives, [selectedFiscalYear]: e.target.checked }
                    }))}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Objectifs mensuels personnalisés</span>
                </label>
              </div>

              {formData.has_custom_objectives[selectedFiscalYear] ? (
                <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-slate-700">
                    Objectifs mensuels {selectedFiscalYear} - {selectedFiscalYear + 1}
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {MONTHS.map((month) => (
                      <div key={month.value}>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {month.label} {parseInt(month.value) <= 3 ? selectedFiscalYear + 1 : selectedFiscalYear}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.monthly_objectives[selectedFiscalYear]?.[month.value] || 0}
                          onChange={(e) => handleMonthlyObjectiveChange(month.value, e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
                    <span className="text-sm font-medium text-indigo-700">Total annuel</span>
                    <span className="text-base font-bold text-indigo-700">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(yearTotal)}
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Objectif annuel (€)</label>
                  <input
                    type="number"
                    value={formData.annual_objectives[selectedFiscalYear]?.annual || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      annual_objectives: {
                        ...prev.annual_objectives,
                        [selectedFiscalYear]: { ...prev.annual_objectives[selectedFiscalYear] || {}, annual: parseFloat(e.target.value) || 0 }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                    min="0"
                    step="1"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Objectif comptable annuel (€)</label>
                <input
                  type="number"
                  value={formData.annual_objectives[selectedFiscalYear]?.accounting || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    annual_objectives: {
                      ...prev.annual_objectives,
                      [selectedFiscalYear]: { ...prev.annual_objectives[selectedFiscalYear] || {}, accounting: parseFloat(e.target.value) || 0 }
                    }
                  }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                  min="0"
                  step="1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rue</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                placeholder="Numéro et nom de rue"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-4 flex justify-end gap-3 shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Chargement…' : (agency ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}